import { useMemo } from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';

import {
  MainWalletPurpose,
  useGetMainWalletBalancesByPurposeQuery,
} from 'src/generated/graphql';
import { varAlpha } from 'src/theme/styles';

// ----------------------------------------------------------------------

const fmtUSD = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const f = (v: string | null | undefined) => parseFloat(v ?? '0') || 0;

const PURPOSE_LABELS: Record<MainWalletPurpose, string> = {
  [MainWalletPurpose.Default]: 'Custodial',
  [MainWalletPurpose.CheddarPay]: 'Checkout',
  [MainWalletPurpose.BatchPayout]: 'Batch Payout',
};

// ----------------------------------------------------------------------

type NavWalletBalanceWidgetProps = {
  organizationId: string;
  compact?: boolean;
};

export function NavWalletBalanceWidget({ organizationId, compact }: NavWalletBalanceWidgetProps) {
  // Read-only totals per purpose. Deliberately not getWallets: that resolver
  // provisions missing main wallets as a side effect, which must not happen
  // from the nav on every page load.
  const [{ data: balancesData, fetching: walletsFetching }] =
    useGetMainWalletBalancesByPurposeQuery({ variables: { organizationId } });

  // The API omits purposes with no wallets. Still list all three, at $0.00.
  const breakdown = useMemo(() => {
    const byPurpose = new Map(
      (balancesData?.getMainWalletBalancesByPurpose ?? []).map((row) => [
        row.purpose,
        f(row.totalBalanceUSD),
      ])
    );
    return (
      [
        MainWalletPurpose.Default,
        MainWalletPurpose.CheddarPay,
        MainWalletPurpose.BatchPayout,
      ] as const
    ).map((purpose) => ({
      key: purpose,
      label: PURPOSE_LABELS[purpose],
      usd: byPurpose.get(purpose) ?? 0,
    }));
  }, [balancesData?.getMainWalletBalancesByPurpose]);

  const totalUsd = useMemo(() => breakdown.reduce((s, row) => s + row.usd, 0), [breakdown]);

  const breakdownRows = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        columnGap: 0.5,
        rowGap: 0.25,
        pt: 1,
        textAlign: 'center',
      }}
    >
      {breakdown.map((row) => (
        <Typography
          key={`${row.key}-label`}
          variant="caption"
          noWrap
          sx={{ color: 'var(--layout-nav-text-disabled-color, text.secondary)' }}
        >
          {row.label}
        </Typography>
      ))}
      {breakdown.map((row) => (
        <Typography key={`${row.key}-usd`} variant="caption" noWrap sx={{ fontWeight: 600 }}>
          {fmtUSD(row.usd)}
        </Typography>
      ))}
    </Box>
  );

  if (compact) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          py: 1,
          px: 0.5,
          borderTop: (t) => `1px solid ${t.vars.palette.divider}`,
          gap: 0.5,
        }}
      >
        <Box sx={{ textAlign: 'center', minWidth: 0, maxWidth: 1 }}>
          <Typography
            variant="caption"
            noWrap
            sx={{
              maxWidth: 1,
              color: 'var(--layout-nav-text-primary-color, text.primary)',
              fontWeight: 700,
            }}
          >
            {walletsFetching ? '…' : fmtUSD(totalUsd)}
          </Typography>
          <Typography
            variant="caption"
            display="block"
            sx={{ color: 'var(--layout-nav-text-disabled-color, text.secondary)', fontSize: '0.65rem' }}
          >
            Wallet
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        flexShrink: 0,
        mx: 2,
        mb: 1.5,
        p: 1.5,
        borderRadius: 2,
        bgcolor: (t) => varAlpha(t.vars.palette.grey['500Channel'], 0.04),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          px: 1,
          pb: 1,
          borderBottom: (t) => `1px solid ${t.vars.palette.divider}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: 'var(--layout-nav-text-disabled-color, text.secondary)' }}
        >
          Wallet balance
        </Typography>
        <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {walletsFetching ? '…' : fmtUSD(totalUsd)}
        </Typography>
      </Stack>
      {breakdownRows}
    </Card>
  );
}
